/**
 * Device Group — Frontend ローカル Contract
 *
 * @standandforce/api-contract publish 前は BE contract をコピーして利用する。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { DeviceGenre } from '@/constants/device';
import { paginatedSchema, paginationQuerySchema } from './pagination';
const DeviceGenreSchema = z.nativeEnum(DeviceGenre);
const RuleModeSchema = z.enum(['ALL', 'NONE', 'CONDITIONAL']);
const JoinOperatorSchema = z.enum(['AND', 'OR']);
const ConditionOpWireSchema = z.enum(['IN', 'NOT IN']);
const ConditionClauseInputSchema = z.object({
  seq: z.number().int().nonnegative(),
  field: z.string(),
  op: ConditionOpWireSchema,
  value: z.union([z.string(), z.array(z.string())])
});
const ConditionFilterInputSchema = z.object({
  joinOperator: JoinOperatorSchema,
  rootFilterId: z.string().nullable().optional(),
  clauses: z.array(ConditionClauseInputSchema)
});
const CategoryRuleInputSchema = z.object({
  category: DeviceGenreSchema,
  mode: RuleModeSchema,
  filter: ConditionFilterInputSchema.nullable().optional()
});
const DeviceGroupBodySchema = z.object({
  name: z.string(),
  intendedUse: z.string().nullable(),
  categoryRules: z.array(CategoryRuleInputSchema),
  includeDeviceIds: z.array(z.string()),
  excludeDeviceIds: z.array(z.string())
});
const CategoryModeSchema = z.object({
  category: DeviceGenreSchema,
  mode: RuleModeSchema
});
const ListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  intendedUse: z.string().nullable(),
  categoryModes: z.array(CategoryModeSchema),
  targetDeviceCount: z.number()
});
const ConditionClauseOutputSchema = z.object({
  seq: z.number(),
  field: z.string(),
  op: ConditionOpWireSchema,
  value: z.union([z.string(), z.array(z.string())])
});
const CategoryRuleDetailSchema = z.object({
  category: DeviceGenreSchema,
  mode: RuleModeSchema,
  conditions: z.array(ConditionClauseOutputSchema)
});
const ReferencedCatalogSchema = z.object({
  id: z.string(),
  name: z.string(),
  genre: DeviceGenreSchema
});
const IndividualDeviceRefSchema = z.object({
  deviceId: z.string(),
  name: z.string()
});
const DetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  intendedUse: z.string().nullable(),
  categoryRules: z.array(CategoryRuleDetailSchema),
  individualIncludeDeviceIds: z.array(z.string()),
  individualExcludeDeviceIds: z.array(z.string()),
  referencedCatalogs: z.array(ReferencedCatalogSchema),
  individualIncludeDevices: z.array(IndividualDeviceRefSchema),
  individualExcludeDevices: z.array(IndividualDeviceRefSchema),
  targetDeviceCount: z.number()
});
const DeviceInGroupSchema = z.object({
  deviceId: z.string(),
  name: z.string(),
  status: z.string(),
  statusBadge: z.object({
    text: z.string(),
    variant: z.string()
  }),
  genre: DeviceGenreSchema
});
const PreviewOutputSchema = z.object({
  devices: z.array(DeviceInGroupSchema),
  countsByGenre: z.array(z.object({
    genre: DeviceGenreSchema,
    count: z.number()
  })),
  totalCount: z.number()
});
const IdOutputSchema = z.object({
  id: z.string()
});
export const deviceGroupContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: '/v2/device-groups'
  }).input(paginationQuerySchema(z.enum(['name'])).extend({
    search: z.string().optional()
  })).output(paginatedSchema(ListItemSchema)),
  get: oc.route({
    method: 'GET',
    path: '/v2/device-groups/{id}'
  }).input(z.object({
    id: z.string()
  })).output(DetailSchema),
  listDevices: oc.route({
    method: 'GET',
    path: '/v2/device-groups/{id}/devices'
  }).input(z.object({
    id: z.string(),
    search: z.string().optional()
  })).output(z.object({
    devices: z.array(DeviceInGroupSchema)
  })),
  create: oc.route({
    method: 'POST',
    path: '/v2/device-groups'
  }).input(DeviceGroupBodySchema).output(IdOutputSchema),
  update: oc.route({
    method: 'PUT',
    path: '/v2/device-groups/{id}'
  }).input(DeviceGroupBodySchema.extend({
    id: z.string()
  })).output(IdOutputSchema),
  delete: oc.route({
    method: 'DELETE',
    path: '/v2/device-groups/{id}'
  }).input(z.object({
    id: z.string()
  })).output(z.object({
    id: z.string()
  })),
  preview: oc.route({
    method: 'POST',
    path: '/v2/device-groups/preview'
  }).input(DeviceGroupBodySchema).output(PreviewOutputSchema)
});
export type DeviceGroupBody = z.infer<typeof DeviceGroupBodySchema>;
export type DeviceGroupDetail = z.infer<typeof DetailSchema>;
export type DeviceGroupListItem = z.infer<typeof ListItemSchema>;
export type DeviceGroupListResult = z.infer<ReturnType<typeof paginatedSchema<typeof ListItemSchema>>>;
export type DeviceInGroup = z.infer<typeof DeviceInGroupSchema>;
export type DeviceGroupPreview = z.infer<typeof PreviewOutputSchema>;
export type ConditionOpWire = z.infer<typeof ConditionOpWireSchema>;
export type RuleMode = z.infer<typeof RuleModeSchema>;